import { api } from './apiClient';
import {
  RangeQuery,
  SalesOverview,
  SalesTrend,
  SalesFunnel,
  SalesPipeline,
  SalesReps,
  SalesMargin,
  SalesCohorts,
  AtRiskDeals,
  SalesTarget,
  TargetInput,
  TargetsQuery,
} from '../types/salesAnalytics';

/**
 * Client for erp-api's /sales-analytics/* endpoints
 * (erp-api/src/routes/salesAnalytics.js).
 *
 * All apiClient verbs (get/post/put/patch/delete) unwrap the server's
 * `{status, data}` envelope down to `data` (see apiClient.ts) — so
 * updateTarget below lands on the same `{ target: SalesTarget }` shape as
 * createTarget.
 */
const salesAnalyticsService = {
  getOverview: (params: RangeQuery): Promise<SalesOverview> =>
    api.get<SalesOverview>('/sales-analytics/overview', { params }),

  getTrend: (params: { metric?: string; days?: number }): Promise<SalesTrend> =>
    api.get<SalesTrend>('/sales-analytics/trend', { params }),

  getFunnel: (params: RangeQuery): Promise<SalesFunnel> =>
    api.get<SalesFunnel>('/sales-analytics/funnel', { params }),

  getPipeline: (params: RangeQuery): Promise<SalesPipeline> =>
    api.get<SalesPipeline>('/sales-analytics/pipeline', { params }),

  getReps: (params: RangeQuery): Promise<SalesReps> =>
    api.get<SalesReps>('/sales-analytics/reps', { params }),

  getMargin: (params: RangeQuery): Promise<SalesMargin> =>
    api.get<SalesMargin>('/sales-analytics/margin', { params }),

  getCohorts: (params: RangeQuery): Promise<SalesCohorts> =>
    api.get<SalesCohorts>('/sales-analytics/cohorts', { params }),

  getAtRisk: (): Promise<AtRiskDeals> => api.get<AtRiskDeals>('/sales-analytics/at-risk'),

  getTargets: (params: TargetsQuery): Promise<{ targets: SalesTarget[]; count: number }> =>
    api.get<{ targets: SalesTarget[]; count: number }>('/sales-analytics/targets', { params }),

  createTarget: async (input: TargetInput): Promise<SalesTarget> => {
    const data = await api.post<{ target: SalesTarget }>('/sales-analytics/targets', input);
    return data.target;
  },

  updateTarget: async (id: number, input: TargetInput): Promise<SalesTarget> => {
    const body = await api.put<{ target: SalesTarget }>(`/sales-analytics/targets/${id}`, input);
    return body.target;
  },
};

export default salesAnalyticsService;
